import { useId } from 'react';
import type { FieldPlacement } from '@/types/template-fields';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';

interface FieldPropertiesPanelProps {
  placement: FieldPlacement | null;
  onUpdate: (updated: FieldPlacement) => void;
  onDelete: (id: string) => void;
  className?: string;
}

export function FieldPropertiesPanel({
  placement,
  onUpdate,
  onDelete,
  className,
}: FieldPropertiesPanelProps) {
  const requiredFieldId = useId();

  if (!placement) {
    return (
      <div className={cn("h-full w-56 shrink-0 border-l bg-background p-4 flex items-center justify-center", className)}>
        <p className="text-sm text-muted-foreground text-center">
          Select a field to edit its properties
        </p>
      </div>
    );
  }

  const isCheckboxField = placement.fieldKey.startsWith('homeowner_checkbox_');

  return (
    <div className={cn("h-full w-56 shrink-0 border-l bg-background", className)}>
      <ScrollArea className="h-full">
        <div className="p-4 space-y-4">
          {/* Label editor for custom/static fields */}
          {(placement.type === 'static_text' || placement.type === 'adjuster_signature' || placement.type === 'text_input' || placement.type === 'signature') && (
            <div>
              <Label className="text-xs">Label</Label>
              <Input
                value={placement.label}
                onChange={(e) =>
                  onUpdate({ ...placement, label: e.target.value })
                }
                className="mt-1 h-8 text-sm"
                placeholder="Field label"
              />
            </div>
          )}

          {/* Required toggle for homeowner-fillable fields */}
          {(placement.type === 'text_input' || placement.type === 'signature') && (
            <div className="flex items-center gap-2">
              <Checkbox
                id={requiredFieldId}
                checked={placement.required ?? true}
                onCheckedChange={(checked) =>
                  onUpdate({ ...placement, required: checked === true })
                }
              />
              <Label htmlFor={requiredFieldId} className="text-xs cursor-pointer">
                Required for {placement.label}
              </Label>
            </div>
          )}

          {/* Static text content preview */}
          {placement.type === 'static_text' && (
            <div>
              <Label className="text-xs">Text Content</Label>
              {placement.staticContent ? (
                <p className="mt-1 text-xs text-muted-foreground bg-muted rounded p-2 whitespace-pre-wrap break-words max-h-24 overflow-y-auto">
                  {placement.staticContent}
                </p>
              ) : (
                <p className="mt-1 text-xs text-muted-foreground italic">
                  No content yet
                </p>
              )}
              <p className="text-xs text-amber-600 mt-1">
                Double-click the field on the canvas to edit text.
              </p>
            </div>
          )}

          {/* Adjuster signature preview */}
          {placement.type === 'adjuster_signature' && (
            <div>
              <Label className="text-xs">Your Signature</Label>
              {placement.staticContent ? (
                <div className="mt-1 space-y-2">
                  <div className="border rounded p-2 bg-white">
                    <img
                      src={placement.staticContent}
                      alt="Your signature"
                      className="max-h-16 w-full object-contain"
                    />
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => onUpdate({ ...placement, staticContent: '' })}
                  >
                    Clear Signature
                  </Button>
                </div>
              ) : (
                <p className="mt-1 text-xs text-emerald-600 italic">
                  Double-click the field on the canvas to sign.
                </p>
              )}
            </div>
          )}

          <div className="space-y-3">
            {/* Font Size */}
            <div>
              <Label className="text-xs">Font Size</Label>
              <Input
                type="number"
                min={4}
                max={72}
                value={placement.fontSize}
                onChange={(e) =>
                  onUpdate({ ...placement, fontSize: Number(e.target.value) })
                }
                className="mt-1 h-8 text-sm"
              />
            </div>

            {/* Font Family */}
            <div>
              <Label className="text-xs">Font Family</Label>
              <select
                value={placement.fontFamily}
                onChange={(e) =>
                  onUpdate({ ...placement, fontFamily: e.target.value })
                }
                className="mt-1 w-full h-8 px-2 text-sm border rounded-md bg-background"
              >
                <option value="Helvetica">Helvetica</option>
                <option value="Times-Roman">Times Roman</option>
                <option value="Courier">Courier</option>
                <option value="Arial">Arial</option>
              </select>
            </div>

            {/* Text Alignment */}
            <div>
              <Label className="text-xs">Alignment</Label>
              <div className="mt-1 flex gap-1">
                {(['left', 'center', 'right'] as const).map((align) => (
                  <button
                    key={align}
                    onClick={() =>
                      onUpdate({ ...placement, textAlign: align })
                    }
                    className={`flex-1 py-1 text-xs rounded border ${
                      placement.textAlign === align
                        ? 'bg-primary/10 border-primary text-primary'
                        : 'border-border text-muted-foreground hover:bg-accent'
                    }`}
                  >
                    {align.charAt(0).toUpperCase() + align.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Position */}
            <div>
              <Label className="text-xs">Position (%)</Label>
              <div className="mt-1 grid grid-cols-2 gap-2">
                <div>
                  <span className="text-xs text-muted-foreground">X</span>
                  <Input
                    type="number"
                    value={Math.round(placement.x * 10) / 10}
                    onChange={(e) =>
                      onUpdate({ ...placement, x: Number(e.target.value) })
                    }
                    className="h-7 text-xs"
                    step={0.5}
                  />
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Y</span>
                  <Input
                    type="number"
                    value={Math.round(placement.y * 10) / 10}
                    onChange={(e) =>
                      onUpdate({ ...placement, y: Number(e.target.value) })
                    }
                    className="h-7 text-xs"
                    step={0.5}
                  />
                </div>
              </div>
            </div>

            {/* Size */}
            <div>
              <Label className="text-xs">Size (%)</Label>
              <div className="mt-1 grid grid-cols-2 gap-2">
                <div>
                  <span className="text-xs text-muted-foreground">W</span>
                  <Input
                    type="number"
                    value={Math.round(placement.width * 10) / 10}
                    onChange={(e) => {
                      const minWidth = isCheckboxField ? 2 : 5;
                      const maxWidth = Math.min(100, 100 - placement.x);
                      const width = Math.max(minWidth, Math.min(maxWidth, Number(e.target.value)));
                      onUpdate({ ...placement, width });
                    }}
                    className="h-7 text-xs"
                    step={0.5}
                    min={isCheckboxField ? 2 : 5}
                  />
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">H</span>
                  <Input
                    type="number"
                    value={Math.round(placement.height * 10) / 10}
                    onChange={(e) => {
                      const minHeight = isCheckboxField ? 1.5 : 2;
                      const maxHeight = Math.min(100, 100 - placement.y);
                      const height = Math.max(minHeight, Math.min(maxHeight, Number(e.target.value)));
                      onUpdate({ ...placement, height });
                    }}
                    className="h-7 text-xs"
                    step={0.5}
                    min={isCheckboxField ? 1.5 : 2}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="pt-2 border-t">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onDelete(placement.id)}
              className="w-full text-destructive hover:text-destructive hover:bg-destructive/10"
            >
              Remove Field
            </Button>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}
